import * as React from "react";
import { AnalyticsStore } from "bernie-plugin-mobx";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkExpandoLink } from "uitk-react-expando";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkPrimaryButton } from "uitk-react-button";
import { AnalyticsConfig } from "uitk-react-utils";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import {
  expandoCollapsedTitle,
  expandoExpandedTitle,
} from "components/flexComponents/Hotels/views/HotelsViews/HotelUtils";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { withStores } from "stores";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";
import { PriceLockupAlign } from "components/shared/PriceLockup/typings";

interface HotelImageCardProps {
  context: ExtendedContextStore;
  model: HotelsFlexModuleResult;
  hotel: Hotel;
  analytics: AnalyticsStore;
}

const HotelCard = withStores("analytics")((props: HotelImageCardProps) => {
  const { hotel, model, context } = props;
  const { callToAction, contentRecipe, destinationId } = model;
  const { hotelId, hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  const [isExpandoVisible, setIsExpandoVisible] = React.useState(false);

  /* covered in e2e test */
  /* istanbul ignore next */
  const expandoLinkAnalytics: AnalyticsConfig = {
    id: "LP.Hotels.HotelImageHalf.ExpandoLink",
    callback: (theId, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: theId,
        action: Action.CLICK,
        linkName: description,
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const ButtonTracked = FlexClickTracker(UitkPrimaryButton);

  return (
    <UitkCard>
      <UitkCardContentSection padded={false}>
        <UitkLayoutGrid columns={2}>
          <UitkFigure ratio={UitkFigureAspectRatioType.R1_1}>
            <UitkImage src={hotelImageUrl} alt={hotel.hotelName} placeholderImage lazyLoading="lazy" />
          </UitkFigure>
          <UitkSpacing padding="two">
            <UitkLayoutFlex direction="column">
              <UitkHeading tag="h3" size={5} overflow="truncate-2-lines">
                {hotelName}
              </UitkHeading>
              <UitkRating
                rating={hotelStarRating}
                icon={ratingIcon(context.locale, context.site.id)}
                className={context.site.brand}
              />
              <UitkText size={300} overflow="truncate-2-lines">
                {hotelFormattedFullAddress}
              </UitkText>
              <UitkSpacing margin={{ blockstart: "three" }}>
                <PriceLockup
                  hotel={hotel}
                  hotelLink={getHotelLink(hotel, model.destinationId, context)}
                  align={PriceLockupAlign.LEFT}
                  priceA11y={new HotelPriceLockup(hotel, formatText)}
                  hasGetRatesText
                />
              </UitkSpacing>
            </UitkLayoutFlex>
          </UitkSpacing>
        </UitkLayoutGrid>

        <UitkCardLink>
          <TrackedLink
            data-testid={`hotelcard-link-${hotel.hotelName}`}
            aria-label={hotelName}
            moduleName="hotels"
            rfrr="card"
            rel={getHotelLinkRel(callToAction)}
            href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
          />
        </UitkCardLink>
      </UitkCardContentSection>

      <UitkCardContentSection border="top" padded={false}>
        <UitkSpacing padding={{ inline: "three" }}>
          <UitkExpandoLink
            isVisible={isExpandoVisible}
            expandoTitle={expandoCollapsedTitle(contentRecipe)}
            expandedLabel={expandoExpandedTitle(contentRecipe)}
            analytics={expandoLinkAnalytics}
            onToggle={() => setIsExpandoVisible((prevIsVisible) => !prevIsVisible)}
            data-testid={`expando-hotelcard-${hotelId}`}
          >
            <UitkSpacing padding={{ block: "three" }}>
              <UitkLayoutFlex direction="column">
                <UitkSpacing margin={{ blockend: "three" }}>
                  <div>
                    <DistanceOrRating model={model} hotel={hotel} context={context} />
                  </div>
                </UitkSpacing>
                <DescriptionOrReview model={model} hotel={hotel} />
                <UitkSpacing margin={{ blockstart: "three" }}>
                  <AmenitiesOrRecommendation model={model} hotel={hotel} />
                </UitkSpacing>
              </UitkLayoutFlex>
            </UitkSpacing>
            <UitkSpacing padding={{ blockstart: "three" }}>
              <UitkCardContentSection padded={false} border="top">
                <UitkSpacing margin={{ blockend: "three" }}>
                  <ValueProposition hotel={hotel} />
                </UitkSpacing>
                <ButtonTracked
                  href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
                  isFullWidth
                  moduleName="LP.hotels.hotelImageHalf"
                  rfrr="expandoExpanded.select"
                >
                  {formatText("hotelsViews.expando.select")}
                </ButtonTracked>
              </UitkCardContentSection>
            </UitkSpacing>
          </UitkExpandoLink>
        </UitkSpacing>
      </UitkCardContentSection>
    </UitkCard>
  );
});

export default HotelCard;
